import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Convert, ReservationElement as Reservation } from "@/models/responses";
import { GridDropdownFilter } from "@/lib/organisms/Grid";
import { ColDef, GridApi, ValueGetterParams } from "ag-grid-community";
import { errorPopup } from "@/utils/alerts";
import ReservationsPage, { ReservationRow } from "../reservations";
import { PeriodName, Platform } from "@/models";
import { euroFormatter, paramsEuroFormatter } from "@/utils/formatters";
import BulkActionConfirmationDialog from "@/lib/shadcn/components/bulk-action-confirmation-modal";
import ShortTermStayUploadProgress from "@/lib/shadcn/components/short-term-stay-upload-progress";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { DiamondPercentIcon, HomeIcon } from "lucide-react";
import UploadToMydataDialog from "@/lib/shadcn/components/upload-to-mydata-dialog";
import { useAllApartmentEntities } from "@/store/slices/apartments";
import { convertToReservation, isFileableToStr, UploadTaskResponse, useUploadTaskMutation } from "@/store/str-api";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { extractErrorMessage } from "@/utils/inUseError";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { booleanSetFilter, labelledSetFilter } from "@/utils/grid";
import {
    NEEDS_REFILE_LABELS,
    NeedsRefileBadgeCell,
    ORIGIN_LABELS,
    OriginBadgeCell,
    RowActionsCell,
    STATUS_LABELS,
    SYNC_LABELS,
    StatusBadgeCell,
    SyncStatusCell,
} from "@/lib/shadcn/components/reservation-grid-cells";
import SyncControlBar from "@/lib/shadcn/components/sync-control-bar";
import ReservationHistoryDialog from "@/lib/shadcn/components/reservation-history-dialog";
import { NewReservationDialog } from "@/lib/shadcn/components/new-reservation-dialog";
import { OverrideOutcome, OverrideReservationDialog } from "@/lib/shadcn/components/override-reservation-dialog";
import { useResumeReservationSyncMutation } from "@/store/booking-management-api";

/**
 * What a saved override did, in words (manual-reservation-cancellation/FR-027).
 * Cancelling and restoring are not corrections and must not read as one: the
 * restore wording carries AS-007's disclosure that the nights and cleaning the
 * cancellation zeroed do not come back.
 */
function outcomeOf(code: string, wasCancelled: boolean, isCancelled: boolean): { announcement: string; toast: string } {
    if (isCancelled && !wasCancelled) {
        return {
            announcement: `Reservation ${code} cancelled. It no longer counts toward occupancy, revenue or the cancellation rate.`,
            toast: `Reservation ${code} was cancelled`,
        };
    }
    if (!isCancelled && wasCancelled) {
        return {
            announcement: `Reservation ${code} restored. Nights and cleaning are 0 until you enter them.`,
            toast: `Reservation ${code} was restored`,
        };
    }
    return {
        announcement: `Reservation ${code} was corrected. Its figures were recalculated and it no longer tracks HostHub.`,
        toast: `Reservation ${code} was corrected`,
    };
}

function strUploadBlockedReason(row: ReservationRow): string | undefined {
    return isFileableToStr(row) ? undefined : "No STR property";
}

export const AdminReservationsPage: React.FC = () => {
    const [reservations, setReservations] = useState<ReservationRow[]>([]);
    const [showStrUploadModal, setShowStrUploadModal] = useState(false);
    const [showMyDataUploadModal, setShowMyDataUploadModal] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [hideCancelled, setHideCancelled] = useState(false);
    const [historyReservation, setHistoryReservation] = useState<{ id: number; reservationCode: string } | null>(null);
    const [overrideReservation, setOverrideReservation] = useState<ReservationRow | null>(null);
    /**
     * What the last completed action did (manual-reservation-override/FR-026,
     * FR-027). A repainted grid is silent to a screen reader: a dialog that just
     * vanishes reads the same as Cancel, and a badge that just disappears reads
     * as nothing at all.
     */
    const [announcement, setAnnouncement] = useState("");

    const { yearOptions, periodOptions, selectedPeriod, selectedYear, groupOptions, selectedGroup } = useAppData();

    const apartmentEntities = useAllApartmentEntities();
    const [uploadTask] = useUploadTaskMutation();
    const [resumeReservationSync] = useResumeReservationSyncMutation();

    const { predicates } = useApartmentFilters();

    /**
     * Apply the shared apartment-filter predicates to the loaded reservation
     * rows. `apartmentIdInternal` is resolved via `apartmentEntities` and fed to
     * every active predicate; rows whose apartment is unknown are excluded under
     * any active filter (same semantics as `pages/dashboards/booking-pace.tsx`).
     */
    const filteredReservations = useMemo(() => {
        let rows = reservations;
        // "Hide cancelled": cancelled shown by default (FR-014,
        // hosthub-reservation-ingestion); this is a client-side opt-in filter.
        if (hideCancelled) {
            rows = rows.filter((row) => !(row.isCancelled ?? false));
        }
        if (predicates.length === 0) {
            return rows;
        }
        return rows.filter((row) => {
            const apartment = apartmentEntities[row.apartmentIdInternal ?? 0];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    }, [reservations, predicates, apartmentEntities, hideCancelled]);

    const { setSelectedYear, setSelectedPeriod, setSelectedGroup } = useUserSettingsDispatch();

    const strUpload = useCallback(async () => {
        const selectedRows: ReservationRow[] = gridApi?.getSelectedRows() || [];
        let resp: UploadTaskResponse = { taskId: 0, requestId: "" };
        try {
            resp = await uploadTask({
                reservations: selectedRows.filter(isFileableToStr).map(convertToReservation),
            }).unwrap();
            setShowStrUploadModal(true);
            return resp.taskId;
        } catch (e) {
            console.error(e);
            toast({
                title: "Error",
                // The backend rejects a batch whose reservations all owe the registry
                // nothing (409). "Failed to upload" would misdescribe that, so surface
                // what it actually said whenever it said anything.
                description: extractErrorMessage(e) ?? "Failed to upload reservations to STR",
                variant: "destructive",
            });
        }
        throw new Error("Failed to upload reservations to STR");
    }, [gridApi, uploadTask]);

    /**
     * The grid is re-read by six different callers, so two reads are routinely
     * in flight at once. A read is dropped once a LATER one has settled — an
     * older read resolving after it would otherwise repaint the grid with rows
     * that predate the newer one.
     *
     * Settled, not applied: a newer read that FAILED has still moved the grid
     * on (its error is what the operator is looking at), so an older read must
     * not fill the gap it left with the previous selection's rows. And settled,
     * not merely ISSUED: the mount issues a second read as soon as the
     * apartments cache lands, and the first read's answer is the only one the
     * grid gets.
     */
    const readsIssued = useRef(0);
    const readsSettled = useRef(0);
    /** The rows the grid is showing, readable between renders. */
    const shownReservations = useRef<ReservationRow[]>([]);

    const getAndSetReservations = useCallback(
        (year: string, period: string, group: string): Promise<void> => {
            const read = ++readsIssued.current;
            const superseded = (): boolean => read < readsSettled.current;
            let url;
            if (group === "0") {
                url = `${apiUrl}/api/admin/reservations?year=${year}&period=${period}`;
            } else {
                url = `${apiUrl}/api/admin/groups/${group}/reservations?year=${year}&period=${period}`;
            }
            return fetch(url, fetchOptions)
                .then((response) => {
                    if (response) {
                        return response.text();
                    }
                    throw new Error("Unauthorized");
                })
                .then((response) => {
                    if (superseded()) {
                        return;
                    }
                    let responseJson = Convert.toGetReservationsResponse(response);
                    let res = responseJson.reservations.map((reservation: Reservation) => {
                        return {
                            id: reservation.id,
                            apartmentIdInternal: reservation.apartmentId,
                            apartment: apartmentEntities[reservation.apartmentId]?.name || "N/A",
                            apartmentStr: apartmentEntities[reservation.apartmentId]?.shortTermRegistryId || "N/A",
                            strApartmentID: apartmentEntities[reservation.apartmentId]?.shortTermPropertyId || 0,
                            platform: Platform[reservation.platform],
                            platformEnum: reservation.platform,
                            guestId: reservation.guestId ? reservation.guestId : reservation.reservationCode,
                            reservationCode: reservation.reservationCode,
                            bookingDate: reservation.bookingDate,
                            startDate: reservation.startDate,
                            endDate: reservation.endDate,
                            actualDaysOfStay: reservation.actualDaysOfStay,
                            guestName: reservation.guestName,
                            numberOfGuests: reservation.numberOfGuests,
                            receivableIncome: reservation.receivableIncome,
                            amountPaid: reservation.amountPaid,
                            paymentDate: reservation.paymentDate,
                            commission: reservation.commission,
                            cleaningFee: reservation.cleaningFee,
                            cleaningCost: reservation.cleaningCost,
                            hostingExpense: reservation.hostingExpense,
                            epsilon: reservation.epsilon,
                            netIncome: reservation.netIncome,
                            grossAdr: reservation.grossAdr,
                            netAdr: reservation.netAdr,
                            vat: reservation.vat,
                            sustainabilityFee: reservation.sustainabilityFee,
                            temporaryStayDuty: reservation.temporaryStayDuty,
                            uploadedToShortTermRegistry: reservation.uploadedToShortTermRegistry,
                            origin: reservation.origin,
                            isCancelled: reservation.isCancelled,
                            cancelledAt: reservation.cancelledAt,
                            needsRefile: reservation.needsRefile,
                            isSyncFrozen: reservation.isSyncFrozen,
                        };
                    });
                    readsSettled.current = read;
                    shownReservations.current = res;
                    setReservations(res);
                })
                .catch((error) => {
                    if (superseded()) {
                        return;
                    }
                    readsSettled.current = read;
                    errorPopup(error.message);
                });
        },
        [apartmentEntities],
    );

    const refreshReservations = useCallback((): Promise<void> => {
        return getAndSetReservations(selectedYear.toString(), selectedPeriod, selectedGroup.toString());
    }, [getAndSetReservations, selectedGroup, selectedPeriod, selectedYear]);

    useEffect(() => {
        void refreshReservations();
    }, [refreshReservations]);

    const pageRef = useRef<HTMLDivElement>(null);
    const rowToRefocus = useRef<string | null>(null);
    const restoreTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    /** A save owns the close it triggers, so that close is not an abandon. */
    const savingOverride = useRef(false);
    /**
     * Which editor the pending restore belongs to. A save's restore waits for
     * the re-read, and the operator can open the next row inside that latency
     * — at which point the earlier restore is for an editor that is already
     * gone, and running it would take the keyboard off the open one and spend
     * the row THAT one has to return to (AS-015).
     */
    const focusGeneration = useRef(0);

    useEffect(() => () => clearTimeout(restoreTimer.current), []);

    /**
     * Put the keyboard back on the row the editor was opened from
     * (manual-reservation-cancellation/AS-015, FR-027).
     *
     * Radix restores focus to the node it snapshotted when the dialog opened,
     * which a save detaches by re-reading the grid, so focus lands on the
     * document. Re-resolve the button instead, after the dialog has had its
     * turn.
     *
     * Reservation codes come from the channels, so the id is escaped before it
     * reaches the selector: an unescaped quote terminates the attribute and the
     * throw escapes the timeout, killing focus restore for every later row
     * (manual-reservation-cancellation/EC-018).
     */
    const restoreFocusToRow = useCallback((rowStillListed: boolean): void => {
        const code = rowToRefocus.current;
        if (code === null) {
            return;
        }
        rowToRefocus.current = null;
        restoreTimer.current = setTimeout(() => {
            // A row the re-read dropped is often still ON SCREEN here — the grid
            // retires its elements over the following frames — so focusing it
            // would land the keyboard on a node about to be detached, which
            // leaves it on the document. Ask the rows, not the DOM.
            const row = rowStillListed
                ? document.querySelector<HTMLElement>(`[data-testid="${CSS.escape(`edit-button-${code}`)}"]`)
                : null;
            // Scrolled out of the rendered window, filtered away, or gone with
            // the re-read: the page is the nearest thing left to tab forward
            // from, and the document is not.
            (row ?? pageRef.current)?.focus();
        });
    }, []);

    /**
     * The override landed: say what it did, repaint, then put the keyboard back
     * (manual-reservation-cancellation/FR-027).
     *
     * The outcome comes from the dialog, which armed it, rather than from the
     * re-read below: that re-read can fail, can answer with rows that predate
     * the save, and is fired by five other things besides this one. Focus waits
     * for the re-read to SETTLE — the repaint detaches the button — but not for
     * it to succeed.
     */
    const onOverridden = useCallback(
        async (outcome: OverrideOutcome) => {
            const generation = focusGeneration.current;
            savingOverride.current = true;
            const { announcement: said, toast: title } = outcomeOf(
                outcome.reservationCode,
                outcome.wasCancelled,
                outcome.isCancelled,
            );
            setAnnouncement(said);
            toast({ title });
            await refreshReservations();
            if (focusGeneration.current !== generation) {
                // A later editor is open and owns the keyboard, including the
                // row it will hand it back to.
                return;
            }
            savingOverride.current = false;
            restoreFocusToRow(shownReservations.current.some((row) => row.reservationCode === outcome.reservationCode));
        },
        [refreshReservations, restoreFocusToRow],
    );

    /** Abandoned, dismissed with Escape or closed on the backdrop: nothing to wait for. */
    useEffect(() => {
        if (overrideReservation !== null || savingOverride.current) {
            return;
        }
        restoreFocusToRow(true);
    }, [overrideReservation, restoreFocusToRow]);

    /**
     * Hand a frozen reservation back to HostHub sync
     * (manual-reservation-override/FR-012). The grid is re-read rather than
     * patched in place: the server answers with its own view of the row.
     */
    const resumeSync = useCallback(
        async (row: ReservationRow) => {
            try {
                await resumeReservationSync(row.id).unwrap();
                void refreshReservations();
                setAnnouncement(`Reservation ${row.reservationCode} is tracking HostHub again.`);
                toast({ title: `Reservation ${row.reservationCode} is tracking HostHub again` });
            } catch (e) {
                toast({
                    title: "Error",
                    description: extractErrorMessage(e) ?? `Failed to resume sync for ${row.reservationCode}`,
                    variant: "destructive",
                });
            }
        },
        [refreshReservations, resumeReservationSync],
    );

    /**
     * The column definitions below are built ONCE (see the memo's note), so they
     * cannot close over `resumeSync`, whose identity changes with the selected
     * period. The ref keeps the colDefs stable while the cell still calls the
     * current handler.
     */
    const resumeSyncRef = useRef(resumeSync);
    useEffect(() => {
        resumeSyncRef.current = resumeSync;
    }, [resumeSync]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            testId: "admin-reservations-filter-period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            testId: "admin-reservations-filter-year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
        {
            label: "Group",
            testId: "admin-reservations-filter-group",
            options: groupOptions,
            selectedOption: selectedGroup.toString(),
            onSelectionChanged: (apartment) => setSelectedGroup(Number(apartment)),
        },
    ];

    const mydataUploadAction = {
        label: "ERP Tax",
        onClick: () => setShowMyDataUploadModal(true),
        icon: DiamondPercentIcon,
        iconClassName: "text-red-500 hover:text-red-700 transition-colors",
    };

    const strUploadAction = {
        label: "Short Stay Registry",
        onClick: () => setShowStrUploadModal(true),
        icon: HomeIcon,
        iconClassName: "text-blue-500 hover:text-blue-700 transition-colors",
    };

    // Memoised as one array: these definitions are static (the only closure is
    // over `setHistoryReservation`, whose identity React guarantees is stable).
    // Rebuilding them per render gave the grid a fresh `columnDefs` identity,
    // which under a saved view that reorders columns makes AG Grid report a
    // `gridOptionsChanged` move — read as the user editing their view, so Save
    // lit up after any unrelated re-render (e.g. selecting a row).
    const extraColumnDefinitions = useMemo<ColDef[]>(() => {
        const cleaningCostColumn: ColDef = {
            field: "cleaningCost",
            headerName: "Cleaning Cost",
            cellStyle: { textAlign: "right" },
            valueFormatter: paramsEuroFormatter,
            width: 110,
            cellDataType: "number",
            cellClass: "currencyFormat",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value: ValueGetterParams) => {
                if (value.data !== undefined && value.data.cleaningCost !== undefined) {
                    return Math.round(value.data.cleaningCost);
                }
            },
        };

        const uploadedToShortTermRegistryColumn: ColDef = {
            field: "uploadedToShortTermRegistry",
            headerName: "STR Done",
            cellStyle: { textAlign: "right" },
            width: 90,
            cellDataType: "boolean",
            cellClass: "boolean",
            // Cell is a checkbox; the filter would otherwise offer raw True/False.
            ...booleanSetFilter("Yes", "No"),
        };

        // HostHub-ingestion grid indicators (ui-spec Screen 1). All text-bearing
        // (never colour-only). Origin, Status (cancelled muted but shown), Re-file,
        // and a per-row History button opening the version-history modal.
        // Each filter is given the SAME labels its badge renderer shows, read from
        // the renderers' own label constants so the two can't drift. The Set Filter
        // never consults `cellRenderer`, so without this it offers the raw
        // underlying value (`True`/`False`, `hosthub`) the badge exists to hide.
        const originColumn: ColDef = {
            field: "origin",
            headerName: "Origin",
            width: 110,
            cellRenderer: OriginBadgeCell,
            // Mirrors OriginBadgeCell: anything that isn't "hosthub" reads as Manual.
            ...labelledSetFilter((value) => (value === "hosthub" ? ORIGIN_LABELS.hosthub : ORIGIN_LABELS.manual)),
        };

        const statusColumn: ColDef = {
            field: "isCancelled",
            headerName: "Status",
            width: 110,
            cellRenderer: StatusBadgeCell,
            ...booleanSetFilter(STATUS_LABELS.cancelled, STATUS_LABELS.active),
        };

        const needsRefileColumn: ColDef = {
            field: "needsRefile",
            headerName: "Re-file",
            width: 130,
            cellRenderer: NeedsRefileBadgeCell,
            // `upToDate` is filter-only by design — the cell renders a bare em-dash
            // there, which no one could pick out of a filter list.
            ...booleanSetFilter(NEEDS_REFILE_LABELS.needed, NEEDS_REFILE_LABELS.upToDate),
        };

        // The sync-freeze mark, status only: its resume action moved to Actions
        // (manual-reservation-cancellation/FR-026). The column stays for its
        // Set Filter on `isSyncFrozen`.
        const syncColumn: ColDef = {
            field: "isSyncFrozen",
            headerName: "Sync",
            width: 150,
            cellRenderer: SyncStatusCell,
            ...booleanSetFilter(SYNC_LABELS.frozen, SYNC_LABELS.tracking),
        };

        // Every row action in one column, pinned right so it cannot scroll away
        // (manual-reservation-cancellation/FR-022, FR-023).
        const actionsColumn: ColDef = {
            colId: "actions",
            headerName: "Actions",
            pinned: "right",
            width: 110,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            lockPinned: true,
            cellRenderer: RowActionsCell,
            cellRendererParams: {
                onOverride: (data: ReservationRow) => {
                    // Remembered on OPEN, so every close route has somewhere to
                    // put the keyboard back (AS-015), and any earlier save whose
                    // re-read never arrived is dropped rather than left pending.
                    rowToRefocus.current = data.reservationCode;
                    savingOverride.current = false;
                    focusGeneration.current++;
                    setOverrideReservation(data);
                },
                onOpenHistory: (data: { id: number; reservationCode: string }) =>
                    setHistoryReservation({ id: data.id, reservationCode: data.reservationCode }),
                onResumeSync: (data: ReservationRow) => void resumeSyncRef.current(data),
            },
        };

        return [
            originColumn,
            statusColumn,
            needsRefileColumn,
            syncColumn,
            cleaningCostColumn,
            uploadedToShortTermRegistryColumn,
            actionsColumn,
        ];
    }, []);

    return (
        <div ref={pageRef} tabIndex={-1} data-testid="admin-reservations-page" className="flex flex-col gap-2">
            <div data-testid="reservations-announcement" role="status" aria-live="polite" className="sr-only">
                {announcement}
            </div>
            <ApartmentFilterBar />
            <div className="flex items-center justify-between gap-2">
                <SyncControlBar
                    hideCancelled={hideCancelled}
                    onHideCancelledChange={setHideCancelled}
                    onSynced={() => void refreshReservations()}
                />
                <NewReservationDialog
                    onCreated={() => void refreshReservations()}
                />
            </div>
            <OverrideReservationDialog
                reservation={overrideReservation}
                onClose={() => setOverrideReservation(null)}
                onOverridden={(outcome) => void onOverridden(outcome)}
            />
            <ReservationHistoryDialog
                open={historyReservation !== null}
                reservationId={historyReservation?.id ?? null}
                reservationCode={historyReservation?.reservationCode ?? ""}
                onClose={() => setHistoryReservation(null)}
            />
            <BulkActionConfirmationDialog<ReservationRow>
                open={showStrUploadModal}
                onClose={() => {
                    void refreshReservations();
                    setShowStrUploadModal(false);
                }}
                onConfirm={strUpload}
                actionName={"Upload to Short Term Registry"}
                ActionIcon={HomeIcon}
                actionIconClassName={"text-blue-500 hover:text-blue-700 transition-colors"}
                selectedRows={gridApi ? gridApi.getSelectedRows() : []}
                blockedReason={strUploadBlockedReason}
                columns={[
                    {
                        header: "Reservation Code",
                        valueGetter: (row) => row.reservationCode,
                    },
                    { header: "Guest", valueGetter: (row) => row.guestName },
                    {
                        header: "Start Date",
                        valueGetter: (row) => row.startDate.toISOString().split("T")[0],
                    },
                    {
                        header: "End Date",
                        valueGetter: (row) => row.endDate.toISOString().split("T")[0],
                    },
                    {
                        header: "Amount",
                        valueGetter: (row) => euroFormatter(row.receivableIncome.toString()),
                    },
                    {
                        header: "Actioned",
                        valueGetter: (row) => (row.uploadedToShortTermRegistry ? "true" : "false"),
                    },
                ]}
                TaskProgress={ShortTermStayUploadProgress}
            ></BulkActionConfirmationDialog>
            <UploadToMydataDialog
                open={showMyDataUploadModal}
                reservationIds={(gridApi ? gridApi.getSelectedRows() : []).map((row) => row.id)}
                onClose={() => {
                    void refreshReservations();
                    setShowMyDataUploadModal(false);
                }}
                onComplete={() => {
                    void refreshReservations();
                }}
            />
            <ReservationsPage
                reservations={filteredReservations}
                onGridReady={setGridApi}
                reservationActions={[mydataUploadAction, strUploadAction]}
                gridDropdownFilters={gridDropdownFilters}
                getRowClass={(params) => (params.data?.isCancelled ? "text-muted-foreground" : undefined)}
                extraColumnDefinitions={extraColumnDefinitions}
            />
        </div>
    );
};
